import { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { Col, Modal, ModalBody, ModalHeader, Row } from "react-bootstrap";

import Icon from "../../../components/Icon";
import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import TableFilter from "../../../components/TableFilter";
import SearchTable from "../../../components/SearchTable";
import EmptyData from "../../../components/EmptyData";
import PaginationComponent from "../../../components/Pagination";
import UserAvatar from "../../../components/UserAvatar";
import Currency, { ConvertCurrency } from "../../../helpers/Currency/Currency";
import Badge from "../../../components/Badge";
import InfoItem from "../../../helpers/InfoItem/InfoItem";
import DataExporter from "../../../components/DataExporter";
import { InvoiceDetails } from "../InvoicesUser/InvoicesUser";
import { paymentUserContext } from "../../../context/UserContext/PaymentUserContext/PaymentUserContext";
import {
  convertInvoiceStatusToColor,
  convertInvoiceStatusToText,
  convertPaymentGatewayToText,
  convertPaymentStatusToColor,
  convertPaymentStatusToText,
  exporterData,
} from "../../../utils/enum";
import { convertDate } from "../../../utils";

const PaymentsUser = () => {
  const auth = useSelector((state) => state.auth);

  const { user: userId, invoice } = useParams();

  const {
    payments,
    payment,
    setPayment,
    filter,
    setFilter,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    status,
    setStatus,
    gateway,
    setGateway,
    pageCount,
    itemsCount,
    setItemsCount,
    pageNumber,
    setPageNumber,
    handleGetPayments,
  } = useContext(paymentUserContext);

  useEffect(() => {
    handleGetPayments();
  }, [
    start,
    end,
    pageNumber,
    itemsCount,
    sort,
    userId,
    status,
    gateway,
    invoice,
  ]);

  return (
    <>
      <SiteTitle title="پرداخت ها" />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
        setPaymentStatus={setStatus}
        paymentStatus={status}
        setPaymentGateway={setGateway}
        paymentGateway={gateway}
      />

      <div className="card h-100 p-0 radius-12">
        <SearchTable
          {...(!isEmpty(payments) && {
            endItem: (
              <DataExporter
                headers={exporterData.payments.headers}
                keys={exporterData.payments.keys}
                data={payments}
                fileName={exporterData.payments.fileName}
              />
            ),
          })}
          filterPlaceholder="کدپیگیری، توضیحات و..."
          filter={filter}
          setFilter={setFilter}
          onSearch={handleGetPayments}
        />
        <div className="card-body p-24">
          {isEmpty(payments) ? (
            <EmptyData />
          ) : (
            <>
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      {auth.isAdmin && <th scope="col">کاربر</th>}
                      <th scope="col">
                        مبلغ <Currency />
                      </th>
                      <th scope="col">وضعیت فاکتور</th>
                      <th scope="col">وضعیت</th>
                      <th scope="col">کد پیگیری</th>
                      <th scope="col">تاریخ ایجاد</th>
                      <th scope="col">تاریخ انقضا</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((item, index) => {
                      return (
                        <tr>
                          <td>{index + 1}</td>
                          {auth.isAdmin && (
                            <td>
                              <div className="d-flex align-items-center">
                                <UserAvatar
                                  className="ms-2"
                                  {...(item?.user?.avatar
                                    ? { image: item?.user?.avatar }
                                    : {
                                        icon: "solar:user-broken",
                                      })}
                                />
                                <div className="flex-grow-1">
                                  <span className="text-md mb-0 fw-normal text-secondary-light">
                                    {item?.user?.firstName}{" "}
                                    {item?.user?.lastName}
                                  </span>
                                </div>
                              </div>
                            </td>
                          )}
                          <td>
                            <ConvertCurrency price={item.amount} />
                          </td>
                          <td>
                            {item?.invoice ? (
                              <Badge
                                className="badge-dim text-sm"
                                color={convertInvoiceStatusToColor(
                                  item?.invoice?.status
                                )}
                              >
                                {convertInvoiceStatusToText(
                                  item?.invoice?.status
                                )}
                              </Badge>
                            ) : (
                              "ندارد"
                            )}
                          </td>
                          <td>
                            <Badge
                              className="badge-dim text-sm"
                              color={convertPaymentStatusToColor(item.status)}
                            >
                              {convertPaymentStatusToText(item.status)}
                            </Badge>
                          </td>
                          <td>{item.trackingCode}</td>
                          <td>
                            {convertDate(
                              item.createdAt,
                              "jYYYY/jMM/jDD - HH:mm:ss"
                            )}
                          </td>
                          <td>
                            {convertDate(
                              item.expiredAt,
                              "jYYYY/jMM/jDD - HH:mm:ss"
                            )}
                          </td>
                          <td>
                            <div className="dropdown">
                              <button
                                className="hover-bg-transparent"
                                status="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                              >
                                <Icon
                                  icon="ph:dots-three-outline-fill"
                                  className="menu-icon"
                                />
                              </button>
                              <ul className="dropdown-menu list">
                                <li
                                  onClick={() => setPayment(item)}
                                  className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                                >
                                  جزئیات
                                </li>
                              </ul>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <PaginationComponent
                pageCount={pageCount}
                currentPage={pageNumber}
                setPageNumber={setPageNumber}
              />
            </>
          )}
        </div>
      </div>

      <Modal
        show={!isEmpty(payment) && !payment.isEdit}
        className="modal-dialog-centered"
        size="lg"
        onHide={() => setPayment({})}
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">جزئیات پرداخت</span>
        </ModalHeader>
        <ModalBody>
          {auth.isAdmin && (
            <>
              <span className="text-primary border-bottom fw-bold">
                اطلاعات کاربر
              </span>
              <Row>
                <Col sm="6">
                  <InfoItem title="نام">{payment?.user?.firstName}</InfoItem>
                </Col>
                <Col sm="6">
                  <InfoItem title="نام خانوادگی">
                    {payment?.user?.lastName}
                  </InfoItem>
                </Col>
                <Col sm="6">
                  <InfoItem title="شماره تماس">
                    {payment?.user?.phoneNumber}
                  </InfoItem>
                </Col>
              </Row>
            </>
          )}

          <span className="text-primary border-bottom fw-bold">
            اطلاعات پرداخت
          </span>
          <Row className="g-2">
            {payment?.gateway && (
              <Col sm="6">
                <InfoItem title="درگاه پرداخت">
                  {convertPaymentGatewayToText(payment?.gateway)}
                </InfoItem>
              </Col>
            )}
            <Col sm="6">
              <InfoItem title="کد پیگیری">{payment?.trackingCode}</InfoItem>
            </Col>
            <Col sm="6">
              <InfoItem title="مبلغ">
                <ConvertCurrency price={payment?.amount} /> <Currency />
              </InfoItem>
            </Col>
            {/* {payment?.cardHash && (
              <Col sm="6">
                <InfoItem title="هش کارت"><p>{payment?.cardHash}</p></InfoItem>
              </Col>
            )} */}
            {payment?.cardPan && (
              <Col sm="6">
                <InfoItem title="شماره کارت">{payment?.cardPan}</InfoItem>
              </Col>
            )}
            {payment?.fee && (
              <Col sm="6">
                <InfoItem title="کارمزد بانک">
                  <ConvertCurrency price={payment?.fee / 10} /> <Currency />
                </InfoItem>
              </Col>
            )}
            {/* {payment?.feeType && (
              <Col sm="6">
                <InfoItem title="پرداخت کننده کارمزد بانک">{payment?.feeType}</InfoItem>
              </Col>
            )} */}
            {payment?.refId && (
              <Col sm="6">
                <InfoItem title="شماره تراكنش">{payment?.refId}</InfoItem>
              </Col>
            )}
            <Col sm="6">
              <InfoItem title="تاریخ ایجاد">
                {convertDate(payment?.createdAt, "jYYYY/jMM/jDD - HH:mm:ss")}
              </InfoItem>
            </Col>
            <Col sm="6">
              <InfoItem title="تاریخ انقضا">
                {convertDate(payment?.expiredAt, "jYYYY/jMM/jDD - HH:mm:ss")}
              </InfoItem>
            </Col>
            <Col sm="6">
              <InfoItem title="وضعیت">
                <Badge
                  className="badge-dim"
                  color={convertPaymentStatusToColor(payment?.status)}
                >
                  {convertPaymentStatusToText(payment?.status)}
                </Badge>
              </InfoItem>
            </Col>
            {!!payment?.description && (
              <Col xs="12">
                <div className="alert alert-info bg-info-50 text-info-500 border-info-100 py-1 mt-1 text-sm radius-10">
                  <div className="d-flex align-items-center gap-2 text-pre-wrap">
                    <Icon icon="solar:danger-triangle-linear" />

                    {payment?.description}
                  </div>
                </div>
              </Col>
            )}
          </Row>

          {payment?.invoice && <InvoiceDetails invoice={payment?.invoice} />}
        </ModalBody>
      </Modal>
    </>
  );
};

export default PaymentsUser;
